import { COMPANY as C } from "../constants/company";

const contact = `${C.legalName}, ${C.address}, ${C.city}, ${C.country} · ${C.email} · ${C.phones[0]}`;

export const legalEnB = [
  {
    slug: "dmca",
    title: "DMCA / Copyright & Content Removal",
    intro: `${C.legalName} respects the intellectual property rights of others and expects users of GOOX to do the same. This document explains how to notify us of alleged copyright infringement and how we handle removal requests, in line with the U.S. Digital Millennium Copyright Act (DMCA), Colombian Law 23 of 1982, Law 1915 of 2018 and applicable international treaties.`,
    sections: [
      { h: "1. Designated agent", body: [
        `Copyright notices should be sent to our designated agent: Copyright Agent, ${C.legalName}, ${C.address}, ${C.city}, ${C.country}. Email: ${C.email} (subject: \"Copyright Notice\").`,
      ]},
      { h: "2. Filing a notice of infringement", body: [
        "To be valid, your notice must include:",
        "- Identification of the copyrighted work claimed to have been infringed.",
        "- The URL or precise location of the allegedly infringing material on GOOX.",
        "- Your name, address, telephone number and email address.",
        "- A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent or the law.",
        "- A statement, under penalty of perjury, that the information is accurate and that you are the owner or authorized to act on the owner's behalf.",
        "- Your physical or electronic signature.",
      ]},
      { h: "3. Our response", body: [
        "Upon receipt of a valid notice we will remove or disable access to the material promptly (target: within 48 hours), notify the creator who posted it and record the notice. Repeat infringers will have their accounts terminated.",
      ]},
      { h: "4. Counter-notice", body: [
        "A creator who believes material was removed by mistake may submit a counter-notice containing: identification of the removed material, a statement under penalty of perjury of good-faith belief that removal was erroneous, consent to the jurisdiction of the competent courts, and a signature. If the complainant does not file a court action within 10–14 business days, the material may be restored.",
      ]},
      { h: "5. Non-copyright removal requests", body: [
        "Persons appearing in content who did not consent to its publication, or who wish to withdraw consent, may request removal through the Complaints / Contact page. Such requests are prioritized and reviewed within 24 hours, and the content is disabled while the review takes place.",
      ]},
      { h: "6. Misrepresentation", body: [
        "Knowingly submitting false claims may result in liability for damages, including costs and attorneys' fees.",
      ]},
      { h: "7. Contact", body: [contact] },
    ],
  },
  {
    slug: "age-safety",
    title: "Age & Safety Policy",
    intro: `GOOX, operated by ${C.legalName}, is a platform exclusively for adults. This Policy summarizes the age-verification and safety controls that apply to the pre-launch Site and that will be enforced on the platform.`,
    sections: [
      { h: "1. Age requirement", body: [
        "Access to the Site and the platform is limited to persons who are at least 18 years old and have reached the age of majority in their jurisdiction. Visitors under 18 must leave immediately.",
      ]},
      { h: "2. Age notice on the Site", body: [
        "The Site displays an 18+ notice in the header, hero and footer. During the pre-launch phase no age-restricted content is shown, and all imagery is brand or conceptual material suitable for a general audience.",
      ]},
      { h: "3. Creator verification (platform)", body: [
        "Before publishing, every creator will complete identity and age verification through a specialized KYC provider, including a government-issued ID check and a liveness/selfie match. Creators must also verify each additional person who appears in their content and keep consent records.",
      ]},
      { h: "4. Fan verification (platform)", body: [
        "Fans will confirm they are adults during registration. Where required by law or by payment partners, GOOX will apply additional age-assurance measures, including document or payment-card based verification.",
      ]},
      { h: "5. Safety controls", body: [
        "- Blocking and reporting tools available on every profile, post and message.",
        "- Geo-blocking options for creators.",
        "- Watermarking and download restrictions to reduce content leakage.",
        "- Prohibition of in-person meeting arrangements through the platform.",
        "- Dedicated trust & safety review of reports within 24 hours for high-risk categories.",
      ]},
      { h: "6. Protection of minors", body: [
        "GOOX has zero tolerance for child sexual abuse material (CSAM). Any such material or attempt to publish it is removed immediately, preserved as evidence and reported to the competent authorities, including the Colombian Police (CAI Virtual) and international bodies such as NCMEC.",
      ]},
      { h: "7. Parental controls", body: [
        "Parents and guardians can restrict access to adult websites using device-level and network-level tools. GOOX will label the platform with RTA-compatible metadata to support filtering software.",
      ]},
      { h: "8. Contact", body: [contact] },
    ],
  },
  {
    slug: "creator-terms",
    title: "Creator Terms",
    intro: `These Creator Terms will govern the relationship between ${C.legalName} and persons or entities who publish content and offer experiences on the GOOX platform ("Creators"). They complement the general Terms & Conditions. Creator onboarding is not yet open; this document is published to describe the planned framework.`,
    sections: [
      { h: "1. Eligibility", body: [
        "- Be at least 18 years old and legally able to enter into contracts.",
        "- Complete identity, age and (where applicable) business verification.",
        "- Provide accurate tax and payout information.",
        "- Agencies and studios must identify every creator they represent and hold written agreements with them.",
      ]},
      { h: "2. Independent relationship", body: [
        "Creators are independent contractors and are solely responsible for their content, taxes and compliance with the law. Nothing in these Terms creates an employment, agency or partnership relationship.",
      ]},
      { h: "3. Content and licenses", body: [
        "Creators retain ownership of their content. By publishing, the Creator grants GOOX a non-exclusive, worldwide license to host, display, distribute and promote the content on the platform for the purpose of providing the service. Creators warrant that they hold all rights and consents required, including from every person appearing in the content.",
      ]},
      { h: "4. Pricing and formats", body: [
        "Creators set the prices of their subscriptions, unlocks, collections, series, exclusive releases and lives within the minimum and maximum limits established by GOOX. All prices must be shown clearly to fans before purchase.",
      ]},
      { h: "5. Fees and payouts", body: [
        "GOOX retains a platform fee expressed as a percentage of each transaction (published in the Creator Studio before onboarding). The remaining amount, net of payment-processing costs and applicable taxes, is credited to the Creator's balance and paid out according to the payout schedule, subject to a holding period for fraud and chargeback protection.",
      ]},
      { h: "6. Chargebacks and refunds", body: [
        "Refunds and chargebacks attributable to the Creator's content or conduct may be deducted from the Creator's balance. GOOX will share the relevant evidence with the Creator.",
      ]},
      { h: "7. Conduct", body: [
        "Creators must comply with the Content Policy, the Acceptable Use Policy and the Age & Safety Policy; must not solicit payments outside the platform for content offered on GOOX; and must not use misleading descriptions, fake scarcity or manipulative practices.",
      ]},
      { h: "8. Suspension and termination", body: [
        "GOOX may suspend or terminate creator accounts for violations of these Terms, upon request of authorities or payment partners, or for risk-management reasons, with notice where feasible. Undisputed balances will be paid after the applicable holding period.",
      ]},
      { h: "9. Taxes", body: [
        "Creators are responsible for declaring and paying taxes on their earnings. GOOX will issue the documentation and apply the withholdings required by Colombian law and the Creator's jurisdiction.",
      ]},
      { h: "10. Contact", body: [contact] },
    ],
  },
  {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    intro: `This Acceptable Use Policy applies to all visitors of ${C.domain} and, once launched, to all users of the GOOX platform operated by ${C.legalName}.`,
    sections: [
      { h: "1. You agree not to", body: [
        "- Use the Site or platform if you are under 18.",
        "- Provide false information, impersonate others or create accounts on behalf of third parties without authorization.",
        "- Upload, share or request content prohibited by the Content Policy.",
        "- Harass, threaten, stalk, dox or discriminate against creators, fans or staff.",
        "- Record, download, screenshot or redistribute content outside the platform without authorization.",
        "- Attempt to bypass payment, age-verification or security systems.",
        "- Use bots, scrapers or automated tools without written permission.",
        "- Introduce malware or interfere with the operation of the service.",
        "- Use the platform for money laundering, fraud, or to arrange in-person sexual services.",
        "- Solicit or offer payments outside the platform for content or experiences offered on GOOX.",
      ]},
      { h: "2. Enforcement", body: [
        "Violations may result in content removal, warnings, temporary suspension, permanent termination, forfeiture of balances where permitted by law, and reporting to authorities.",
      ]},
      { h: "3. Reporting", body: [
        `Report abuse through the Complaints / Contact page or at ${C.email}.`,
      ]},
      { h: "4. Contact", body: [contact] },
    ],
  },
  {
    slug: "complaints",
    title: "Complaints / Contact",
    intro: `${C.legalName} operates GOOX. This page provides the channels to contact us, file a complaint, report content or exercise your data-protection rights.`,
    sections: [
      { h: "1. Contact details", body: [
        `Company: ${C.legalName} · NIT ${C.nit}`,
        `Commercial registry: No. ${C.registry}, ${C.chamber}`,
        `Address: ${C.address}, ${C.city}, ${C.country}`,
        `Email: ${C.email}`,
        `Phone: ${C.phones.join(" · ")}`,
        `Address for judicial notifications: ${C.address}, ${C.city}, ${C.country} · ${C.email}`,
      ]},
      { h: "2. Types of requests", body: [
        "- General inquiries and business proposals.",
        "- Complaints about the Site or, in the future, about transactions, creators or content.",
        "- Content reports (prohibited content, non-consensual material, copyright).",
        "- Data-protection requests (access, rectification, deletion, revocation of authorization).",
        "- Requests from authorities and payment partners.",
      ]},
      { h: "3. Response times", body: [
        "- Reports involving minors or non-consensual content: reviewed within 24 hours.",
        "- Copyright notices: acted upon within 48 hours.",
        "- Data-protection inquiries: 10 business days; claims: 15 business days (Law 1581 of 2012).",
        "- Other complaints: 5 business days for a first response.",
      ]},
      { h: "4. How to file a complaint", body: [
        `Email ${C.email} with the subject \"Complaint\" and include: your name and contact details, a description of the issue, the URL or reference involved and any supporting evidence. We will confirm receipt and provide a reference number.`,
      ]},
      { h: "5. Escalation", body: [
        "If you are not satisfied with our response, you may file a complaint before the Superintendencia de Industria y Comercio (SIC) in Colombia or the competent consumer or data-protection authority in your jurisdiction.",
      ]},
    ],
  },
];
